import { useMemo } from 'react';

import deselectedColor from 'hooks/useChartDrillDowns/deselectedColor';
import cssTokens, { darkCssTokens } from 'theme/tokens';
import useDarkModeToggle from 'theme/useDarkModeToggle';

const FONT_FAMILY = "Inter, 'Roboto', arial, sans-serif";

// The tokens a block of token CSS sets, by name
const parseTokens = (css: string): Record<string, string> =>
  Object.fromEntries(
    Array.from(css.matchAll(/(--[\w-]+)\s*:\s*([^;]+);/g)).map(
      ([, name, value]) => [name, value.trim()],
    ),
  );

const LIGHT_TOKENS = parseTokens(cssTokens);
const DARK_TOKENS = { ...LIGHT_TOKENS, ...parseTokens(darkCssTokens) };

// Charts need real colours (they fade and blend them), not var(--…)
// references, so they're read from the token definitions themselves: the
// same in tests, and before the page's global styles have mounted
export const getChartTheme = (isDarkMode: boolean): StudioCharts.Theme => {
  const tokens = isDarkMode ? DARK_TOKENS : LIGHT_TOKENS;
  return {
    fontFamily: FONT_FAMILY,
    textPrimary: tokens['--color-text-primary'],
    textMuted: tokens['--color-text-muted'],
    axisLine: tokens['--color-border-secondary'],
    gridLine: tokens['--color-border-secondary'],
    tooltipBackground: tokens['--color-tooltip-bg'],
    tooltipText: tokens['--color-tooltip-text'],
    tooltipBorder: tokens['--color-border-secondary'],
    // The Highcharts charts draw trend lines orange
    trend: tokens['--color-category-orange-start'],
    deselected: deselectedColor,
    track: tokens['--color-bg-soft'],
    crosshair: tokens['--color-border-tertiary'],
    // As StudioChart styles the Highcharts forecasts
    forecastPeriod: tokens['--color-bg-secondary'],
    forecastStart: tokens['--color-border-primary'],
    background: tokens['--color-bg-primary'],
    // The chart colours are as pale in both modes, so take dark text
    textOnColor: LIGHT_TOKENS['--color-text-primary'],
    // As the Highcharts gauge colours itself
    danger: tokens['--color-status-danger'],
    warning: tokens['--color-status-warning'],
    success: tokens['--color-status-success'],
    gaugeTrack: tokens['--color-bg-tertiary'],
    warningText: tokens['--color-badge-yellow-text'],
    // As the Highcharts charts ring their labels in dark mode
    ...(isDarkMode ? { labelHalo: tokens['--color-bg-primary'] } : {}),
  };
};

// The chart theme for the app's current light or dark mode
const useChartTheme = (): StudioCharts.Theme => {
  const { isDarkMode } = useDarkModeToggle();
  return useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
};

export default useChartTheme;
